import { Component } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { GateBlockResponse } from 'src/app/core/models/gate-override-request';

/**
 * The stop screen shown when the gate tries to admit a flagged truck (MDP Operations, 2026-09-17).
 *
 * Deliberately a BLOCKING modal, not a toast. The existing AlertService shows validation failures as
 * a toast that fades after five seconds — fine for "this field is required", useless for "do not let
 * this truck in and phone the office". The gateman has a driver at the window and needs something
 * that stays on screen until acknowledged, which is why the caller opens it with a static backdrop
 * and keyboard:false.
 *
 * It offers no way through. There is no "admit anyway" button anywhere in this feature — the only
 * routes are Operations approving the arrival or lifting the flag, both recorded against a name.
 *
 * Plain public properties rather than @Input(), matching ConfirmModalComponent: these are assigned
 * directly on componentInstance by the opener. The defaults are a genuine fail-safe — if the body
 * ever arrived malformed, the screen must still say DO NOT ADMIT rather than render blank.
 */
@Component({
  selector: 'app-gate-block-modal',
  templateUrl: './gate-block-modal.component.html',
  styleUrls: ['./gate-block-modal.component.scss'],
})
export class GateBlockModalComponent {
  block: GateBlockResponse = {
    message: 'This truck is flagged and cannot be checked in.',
    blocked: true,
    flag: {
      uuid: '',
      reason: 'Flagged by Operations.',
      attempt_count: 0,
    },
    override_request: null,
    instruction: 'CALL OPERATIONS',
  };

  constructor(public activeModal: NgbActiveModal) {}

  get registration(): string {
    return this.block.flag.registration_number || this.block.flag.driver_name || 'This truck';
  }

  get reference(): string {
    return this.block.override_request?.reference ?? '';
  }

  /** Operations have already turned this truck away — show their words, not a bare refusal. */
  get declined(): boolean {
    return this.block.override_request?.status === 'DECLINED';
  }

  get declineRemark(): string {
    return this.declined ? this.block.override_request?.remarks ?? '' : '';
  }

  get repeatAttempts(): number {
    return this.block.flag.attempt_count ?? 0;
  }
}
